import { useUserContext } from '../hooks';
import { useCallback } from 'react';
import { track as amplitudeTrack } from '@amplitude/analytics-browser';

/**
 * @description Use for tracking metrics defined in Amplitude.
 * @returns {() => void} A callback function that can be used to track specific metrics defined in Amplitude.
 * @param {string} metricKey - The key of the metric to be tracked.
 * @param {boolean} [useUserActivationId] (optional) - override the activation context to be the user.
 * @param {Object} [audienceAttributes] - Optional attributes for audience targeting.
 * @example <caption>Track clicks on a button</caption>
 * const trackClick = useTrackExperimentEvent({ metricKey: 'some_metric' });
 * <button onClick={trackClick}>click me</button>
 **/

interface TrackExperimentEventProps {
  metricKey: string;
  useUserActivationId?: boolean;
  audienceAttributes?: { [name: string]: any };
}

type TrackExperimentEventCallback = () => void;

export const useTrackExperimentEvent = ({
  metricKey,
  useUserActivationId = false,
  audienceAttributes
}: TrackExperimentEventProps): TrackExperimentEventCallback => {
  const { user, loading: loadingUser } = useUserContext();

  const trackExperimentEvent = useCallback(() => {
    if (loadingUser) return;

    const activationId = useUserActivationId
      ? `do:user:${user.uuid}`
      : user.current_context_urn;

    amplitudeTrack({
      user_id: user.uuid,
      event_type: metricKey,
      event_properties: {
        activationId,
        audienceAttributes
      },
      groups: {
        team_id: user.current_context_id
      }
    });
  }, [user, loadingUser, useUserActivationId, audienceAttributes]);

  return trackExperimentEvent;
};
